import { TabGroup, TabPanel, TabPanels } from "@headlessui/react";
import { generateId } from "@msuite/utils/strings";
import type { VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { useQueryState } from "nuqs";
import { useId, useRef } from "react";
import type { ClassNameProps } from "../types";
import { TabsContext } from "./context";
import { tabClasses } from "./shared";
import { Tab } from "./tab";
import { TabList } from "./tab-list";

export interface TabPanelTypeItem {
	label: string;
	panel: () => React.JSX.Element;
	isDisabled?: boolean;
	count?: number | string;
	errors: string[] | null;
	rightIcon?: React.ReactNode;
	cyId: `${string}-tab`;
}

export interface TabPanelGroup {
	name: string;
	panels: Array<TabPanelTypeItem | null>;
}

export type TabPanelGroups = Array<TabPanelGroup | "spacer">;

/** Props Interface */
interface TabsProps extends ClassNameProps, VariantProps<typeof tabClasses> {
	groups: TabPanelGroups;
	panelKey?: `${string}-tabs`;
	header?: () => React.ReactNode;
	withHeader?: boolean;
}

export interface TabHandle {
	activeIndex: string;
	setActiveIndex: (index: string) => void;
	nextTab: () => void;
	previousTab: () => void;
}

export function Tabs({
	className,
	groups,
	panelKey = "panel-tabs",
	header = () => null,
	withHeader = true,
	variant,
}: TabsProps) {
	/** State */
	const [activeIndex, setActiveIndex] = useQueryState(panelKey, {
		defaultValue: "0",
		clearOnDefault: false,
	});

	/** Hooks */
	const headerRef = useRef<HTMLDivElement | null>(null);
	const tabsId = useId();

	/** Classes */
	const classes = tabClasses({ variant });

	/** Render */
	return (
		<TabsContext.Provider value={{ tabsId, headerRef }}>
			<TabGroup
				className={clsx("flex flex-col items-start h-full min-h-0", classes, className)}
				tabIndex={Number(activeIndex)}
				defaultIndex={Number(activeIndex)}
				onChange={(index) => setActiveIndex(index.toString())}
			>
				<div className="gap-0 w-full h-stack px-2">
					<div className="flex-1 h-stack">
						{groups.map((group) =>
							group === "spacer" ? (
								<div
									className="flex-1"
									key={generateId()}
								/>
							) : (
								<TabList
									key={group.name}
									variant={variant}
								>
									{group.panels.filter(Boolean).map((panel) =>
										panel ? (
											<Tab
												key={panel.label}
												isDisabled={panel.isDisabled}
												count={panel.count}
												errors={panel.errors?.filter(Boolean) as string[]}
												rightIcon={panel.rightIcon}
												cyId={panel.cyId}
											>
												{panel.label}
											</Tab>
										) : null,
									)}
								</TabList>
							),
						)}
					</div>
					{withHeader && (
						<div
							ref={headerRef}
							className="pl-4"
						>
							{header?.()}
						</div>
					)}
				</div>
				<TabPanels className="relative flex grow-0 py-1 w-full h-full px-2 overflow-y-auto overflow-x-hidden">
					{groups
						.filter((group) => group !== "spacer")
						.flatMap((group) => group.panels)
						.filter(Boolean)
						.map((panel) =>
							panel ? (
								<TabPanel
									key={panel.label}
									className="w-full h-full"
								>
									{!panel.isDisabled ? panel.panel() : null}
								</TabPanel>
							) : null,
						)}
				</TabPanels>
			</TabGroup>
		</TabsContext.Provider>
	);
}
